import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  CircularProgress,
  Grid,
  Divider,
  CardActions,
  Paper,
  Stack,
  Alert,
  Snackbar,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import OpacityIcon from '@mui/icons-material/Opacity';
import AirIcon from '@mui/icons-material/Air';
import CompressIcon from '@mui/icons-material/Compress';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import CloudIcon from '@mui/icons-material/Cloud';
import WbCloudyIcon from '@mui/icons-material/WbCloudy';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import UmbrellaIcon from '@mui/icons-material/Umbrella';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import RefreshIcon from '@mui/icons-material/Refresh';
import MyLocationIcon from '@mui/icons-material/MyLocation';

// OpenWeatherMap API key - in production, this should be secured in environment variables
const API_KEY = 'YOUR_OPENWEATHERMAP_API_KEY'; // Replace with your actual API key
const WEATHER_API_URL = 'https://api.openweathermap.org/data/2.5';

const WeatherCard = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forecast, setForecast] = useState(null);
  const [rainAlert, setRainAlert] = useState(null);
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [useRealData, setUseRealData] = useState(false);

  // Set default empty weather state
  const setEmptyWeatherData = () => {
    setWeather(null);
    setForecast(null);
    setRainAlert(null);
    setError('Weather data not available. Enable real weather data to see current conditions.');
    setLoading(false);
  };

  // Function to get user's location
  const getUserLocation = () => {
    setLoading(true);
    setError(null);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ lat: latitude, lon: longitude });
          fetchWeatherData(latitude, longitude);
        },
        (err) => {
          console.error('Error getting location:', err);
          setError('Unable to access your location.');
          setEmptyWeatherData();
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
      );
    } else {
      setError('Geolocation is not supported by your browser.');
      setEmptyWeatherData();
    }
  };

  // Function to fetch weather data from OpenWeatherMap API
  const fetchWeatherData = async (lat, lon) => {
    try {
      // Current weather data
      const weatherResponse = await fetch(
        `${WEATHER_API_URL}/weather?lat=${lat}&lon=${lon}&units=metric&appid=${API_KEY}`
      );
      
      if (!weatherResponse.ok) {
        throw new Error('Weather data fetch failed');
      }
      
      const weatherData = await weatherResponse.json();
      
      // One Call API for forecast and minutely precipitation (rain alarm)
      const oneCallResponse = await fetch(
        `${WEATHER_API_URL}/onecall?lat=${lat}&lon=${lon}&units=metric&exclude=hourly&appid=${API_KEY}`
      );
      
      if (!oneCallResponse.ok) {
        throw new Error('Forecast data fetch failed');
      }
      
      const oneCallData = await oneCallResponse.json();
      
      // Format current weather data
      const formattedWeather = {
        name: weatherData.name,
        day: new Date().toLocaleDateString('en-US', { weekday: 'long' }),
        date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' }),
        main: {
          temp: weatherData.main.temp,
          humidity: weatherData.main.humidity,
          pressure: weatherData.main.pressure,
          feels_like: weatherData.main.feels_like,
          temp_max: weatherData.main.temp_max,
          temp_min: weatherData.main.temp_min
        },
        weather: weatherData.weather,
        wind: { speed: weatherData.wind.speed * 3.6 }, // Convert m/s to km/h
        predictability: 85, // Default value as OpenWeatherMap doesn't provide this
      };
      
      // Format 5-day forecast
      const formattedForecast = oneCallData.daily.slice(1, 6).map(day => {
        return {
          day: new Date(day.dt * 1000).toLocaleDateString('en-US', { weekday: 'short' }),
          temp: Math.round(day.temp.day),
          icon: mapWeatherConditionToIcon(day.weather[0].main),
          condition: day.weather[0].main
        };
      });
      
      // Check for rain alerts based on minutely data
      let rainAlertInfo = null;
      
      if (oneCallData.minutely) {
        const nextHourPrecipitation = oneCallData.minutely.reduce((sum, minute) => sum + minute.precipitation, 0);
        
        if (nextHourPrecipitation > 0) {
          const firstRainMinute = oneCallData.minutely.findIndex(minute => minute.precipitation > 0);
          
          if (firstRainMinute !== -1 && firstRainMinute < 60) {
            const timeUntilRain = firstRainMinute === 0 ? 'now' : `in ${firstRainMinute} minute${firstRainMinute > 1 ? 's' : ''}`;
            const intensity = nextHourPrecipitation > 5 ? 'heavy' : nextHourPrecipitation > 2 ? 'moderate' : 'light';
            
            rainAlertInfo = {
              willRain: true,
              timeUntilRain,
              intensity,
              totalPrecipitation: nextHourPrecipitation.toFixed(1)
            };
            
            // Show notification
            setAlertOpen(true);
          }
        }
      }
      
      setWeather(formattedWeather);
      setForecast(formattedForecast);
      setRainAlert(rainAlertInfo);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching weather data:', err);
      setError('Unable to fetch real-time weather data.');
      setEmptyWeatherData();
    }
  };

  // Map weather condition to icon type
  const mapWeatherConditionToIcon = (condition) => {
    const lowerCondition = condition.toLowerCase();
    if (lowerCondition.includes('clear')) return 'sunny';
    if (lowerCondition.includes('cloud')) {
      if (lowerCondition.includes('few') || lowerCondition.includes('scattered')) return 'sunny-clouds';
      return 'clouds';
    }
    if (lowerCondition.includes('rain') || lowerCondition.includes('drizzle') || lowerCondition.includes('shower')) return 'rain';
    if (lowerCondition.includes('snow')) return 'snow';
    if (lowerCondition.includes('thunderstorm')) return 'thunder';
    return 'clouds'; // default
  };

  // Function to get weather data
  const getCurrentWeather = () => {
    setLoading(true);
    
    if (useRealData && navigator.onLine) {
      // Try to get real data if user has opted for real data
      getUserLocation();
    } else {
      // Set empty data when not using real data
      setTimeout(() => {
        setEmptyWeatherData();
      }, 800); // Short delay to simulate loading
    }
  };

  // Get weather icon based on condition
  const getWeatherIcon = (condition) => {
    switch (condition) {
      case 'sunny':
        return <WbSunnyIcon sx={{ fontSize: 40, color: '#FFB300' }} />;
      case 'clouds':
        return <WbCloudyIcon sx={{ fontSize: 40, color: '#E0E0E0' }} />;
      case 'rain':
        return <ThunderstormIcon sx={{ fontSize: 40, color: '#90CAF9' }} />;
      case 'snow':
        return <AcUnitIcon sx={{ fontSize: 40, color: '#E0E0E0' }} />;
      case 'thunder':
        return <ThunderstormIcon sx={{ fontSize: 40, color: '#FFD54F' }} />;
      case 'sunny-clouds':
        return (
          <Box sx={{ position: 'relative' }}>
            <WbSunnyIcon sx={{ fontSize: 40, color: '#FFB300', position: 'absolute', left: '-10px' }} />
            <CloudIcon sx={{ fontSize: 30, color: '#E0E0E0', position: 'relative', top: '5px', left: '5px' }} />
          </Box>
        );
      default:
        return <WbCloudyIcon sx={{ fontSize: 40, color: '#E0E0E0' }} />;
    }
  };

  // Close alert
  const handleCloseAlert = () => {
    setAlertOpen(false);
  };

  // Toggle between real and demo data
  const handleToggleDataSource = (event) => {
    setUseRealData(event.target.checked);
    setLoading(true);
    
    if (event.target.checked) {
      setError('Attempting to fetch real-time data. This may not work if API key is not set.');
      getUserLocation();
    } else {
      setEmptyWeatherData();
    }
  };

  // Get weather on component mount
  useEffect(() => {
      // Initial load of weather data - set empty by default
  setEmptyWeatherData();
  }, []);

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', mb: 2 }}>
        <Typography variant="h4" align="center" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          Weather <Typography variant="h4" component="span" sx={{ ml: 1, fontWeight: 'bold' }}>Forecast</Typography>
        </Typography>
        
        <FormControlLabel
          control={
            <Switch 
              checked={useRealData} 
              onChange={handleToggleDataSource} 
              size="small"
              color="primary"
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              {useRealData ? "Using real-time data" : "Using demo data"}
            </Typography>
          }
        />
      </Box>
      
      {error && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      ) : weather ? (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, borderRadius: 4, overflow: 'hidden' }}>
          {/* Left side - Current weather with gradient background */}
          <Card 
            sx={{ 
              flex: { xs: '1', md: '0 0 250px' }, 
              background: 'linear-gradient(135deg, #64B5F6 0%, #2196F3 100%)',
              color: 'white',
              borderRadius: 4,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                opacity: 0.15,
                backgroundImage: 'url("https://images.unsplash.com/photo-1516912481808-3406841bd33c?q=80&w=2070")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                zIndex: 0
              }} 
            />
            <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {weather.day}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
                    {weather.date}
                  </Typography>
                </Box>
                <Button 
                  variant="text" 
                  color="inherit" 
                  size="small" 
                  onClick={getCurrentWeather}
                  disabled={loading}
                  sx={{ color: 'white' }}
                >
                  <RefreshIcon />
                </Button>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationOnIcon sx={{ mr: 0.5, fontSize: 18 }} />
                <Typography variant="body2">
                  {weather.name}
                </Typography>
                {useRealData && !location && (
                  <Button 
                    size="small" 
                    variant="text" 
                    color="inherit"
                    onClick={getUserLocation}
                    sx={{ ml: 1, minWidth: 'auto', p: 0.5 }}
                  >
                    <MyLocationIcon fontSize="small" />
                  </Button>
                )}
              </Box>
              
              {rainAlert && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Chip
                    icon={<UmbrellaIcon />}
                    label={`Rain ${rainAlert.timeUntilRain}`}
                    color="warning"
                    size="small"
                    sx={{ 
                      bgcolor: 'rgba(0,0,0,0.2)', 
                      color: 'white',
                      fontWeight: 'bold',
                      '& .MuiChip-icon': { color: 'white' }
                    }}
                  />
                </Box>
              )}
              
              <Box sx={{ mt: 4, mb: 1 }}>
                <Typography variant="h1" sx={{ fontWeight: 'bold', fontSize: '4rem', lineHeight: 1 }}>
                  {Math.round(weather.main.temp)}°C
                </Typography>
                <Typography variant="h6" sx={{ mt: 1 }}>
                  {weather.weather[0].main}
                </Typography>
              </Box>
            </CardContent>
          </Card>
          
          {/* Right side - Weather details and forecast */}
          <Paper 
            elevation={0} 
            sx={{ 
              flex: '1', 
              bgcolor: '#2A2F3D', 
              color: 'white',
              borderRadius: 4,
              p: 3,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Rain alarm section */}
            {rainAlert && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(255, 152, 0, 0.15)', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <NotificationsActiveIcon sx={{ mr: 1, color: '#FFB74D' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Rain Alert
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {rainAlert.intensity.charAt(0).toUpperCase() + rainAlert.intensity.slice(1)} rain expected {rainAlert.timeUntilRain.toLowerCase()}.
                  Total precipitation: {rainAlert.totalPrecipitation}mm
                </Typography>
              </Box>
            )}
            
            {/* Weather stats */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={6} md={4}>
                <Box>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)" gutterBottom>
                    PREDICTABILITY
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    {weather.predictability} %
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={4}>
                <Box>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)" gutterBottom>
                    HUMIDITY
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    {weather.main.humidity} %
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={4}>
                <Box>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)" gutterBottom>
                    WIND
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    {Math.round(weather.wind.speed)} km/h
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={4}>
                <Box>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)" gutterBottom>
                    AIR PRESSURE
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    {weather.main.pressure} mb
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={4}>
                <Box>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)" gutterBottom>
                    MAX TEMP
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    {Math.round(weather.main.temp_max)} °C
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={4}>
                <Box>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)" gutterBottom>
                    MIN TEMP
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    {Math.round(weather.main.temp_min)} °C
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            {/* Forecast */}
            {forecast && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                {forecast.map((day, index) => (
                  <Box key={index} sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" gutterBottom>
                      {day.day}
                    </Typography>
                    {getWeatherIcon(day.icon)}
                    <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold' }}>
                      {day.temp}°
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Box>
      ) : (
        <Typography color="text.secondary" sx={{ my: 2, textAlign: 'center' }}>
          No weather data available
        </Typography>
      )}
      
      {/* Rain Alert Notification */}
      <Snackbar 
        open={alertOpen && rainAlert} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="warning" 
          variant="filled"
          icon={<UmbrellaIcon />}
          onClose={handleCloseAlert}
          sx={{ width: '100%' }}
        >
          {rainAlert && `${rainAlert.intensity.charAt(0).toUpperCase() + rainAlert.intensity.slice(1)} rain expected ${rainAlert.timeUntilRain.toLowerCase()}`}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WeatherCard; 